import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit2, Trash2, LayoutGrid, List, Star, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Trans, useTranslation } from 'react-i18next';
import { TruncatedText } from '../components/ui/truncated-text';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROJECT_TYPES = [
  { value: 'client', labelKey: 'types.client' },
  { value: 'internal', labelKey: 'types.internal' },
  { value: 'outsource', labelKey: 'types.outsource' },
  { value: 'saas', labelKey: 'types.saas' },
  { value: 'ecommerce', labelKey: 'types.ecommerce' },
  { value: 'other', labelKey: 'types.other' },
];

const STATUS_STYLES = {
  active: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400',
  paused: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400',
  completed: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400',
};

const TYPE_STYLES = {
  client: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
  internal: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400',
  outsource: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400',
  saas: 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400',
  ecommerce: 'bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-400',
  other: 'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400',
};

function StarRating({ rating, onChange, readonly = false }) {
  const { t } = useTranslation('projects');
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          aria-label={t('starLabel', { count: star })}
          aria-pressed={star <= rating}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-colors`}
          onClick={() => !readonly && onChange?.(star === rating ? 0 : star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
        >
          <Star
            className={`h-4 w-4 ${
              star <= (hover || rating)
                ? 'fill-amber-400 text-amber-400'
                : 'fill-none text-slate-300 dark:text-slate-600'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

const STATUS_TABS = [
  { value: 'active', labelKey: 'tabs.active' },
  { value: 'paused', labelKey: 'tabs.paused' },
  { value: 'completed', labelKey: 'tabs.completed' },
  { value: 'all', labelKey: 'common:actions.all' },
];

function Projects() {
  const { t } = useTranslation('projects');
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isReadOnly = currentUser?.role === 'employee';

  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [statusFilter, setStatusFilter] = useState('active');
  const [formData, setFormData] = useState({
    name: '', description: '', domain: '', project_type: '', merchant_name: '', owner_employee_id: '', rating: 0, status: 'active'
  });

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const projectsRes = await axios.get(`${API_URL}/api/projects`, { withCredentials: true });
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
    try {
      const employeesRes = await axios.get(`${API_URL}/api/employees?per_page=100&status=active`, { withCredentials: true });
      setEmployees(employeesRes.data?.items || employeesRes.data?.employees || []);
    } catch (err) {
      console.error('Employees fetch error:', err?.response?.status, err?.response?.data);
    }
  }, [t]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Open the edit dialog automatically when navigated here from the detail
  // page's "Edit" button (router state carries the target project id).
  useEffect(() => {
    const editId = location.state?.editProjectId;
    if (editId && projects.length > 0) {
      const target = projects.find((p) => p.project_id === editId);
      if (target) {
        openEditDialog(target);
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, projects]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        project_type: formData.project_type || null,
        merchant_name: formData.merchant_name ? formData.merchant_name.replace(/📍/g, '').trim() : null,
        owner_employee_id: formData.owner_employee_id || null,
        rating: formData.rating || null,
        domain: formData.domain || null,
      };
      if (editingProject) {
        await axios.patch(`${API_URL}/api/projects/${editingProject.project_id}`, payload, { withCredentials: true });
        toast.success(t('toast.updated'));
      } else {
        await axios.post(`${API_URL}/api/projects`, payload, { withCredentials: true });
        toast.success(t('toast.created'));
      }
      setOpen(false);
      setEditingProject(null);
      setFormData({ name: '', description: '', domain: '', project_type: '', merchant_name: '', owner_employee_id: '', rating: 0, status: 'active' });
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error(t('toast.saveFailed'));
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm(t('toast.confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${projectId}`, { withCredentials: true });
      toast.success(t('toast.deleted'));
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      const errorMessage = error.response?.data?.detail || t('toast.deleteFailed');
      toast.error(errorMessage, { duration: 6000 });
    }
  };

  const openEditDialog = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      domain: project.domain || '',
      project_type: project.project_type || '',
      merchant_name: project.merchant_name || '',
      owner_employee_id: project.owner_employee_id || '',
      rating: project.rating || 0,
      status: project.status,
    });
    setOpen(true);
  };

  const openAddDialog = () => {
    setEditingProject(null);
    setFormData({ name: '', description: '', domain: '', project_type: '', merchant_name: '', owner_employee_id: '', rating: 0, status: 'active' });
    setOpen(true);
  };

  const statusCounts = useMemo(() => {
    const counts = { active: 0, paused: 0, completed: 0, all: projects.length };
    projects.forEach((p) => { counts[p.status] = (counts[p.status] || 0) + 1; });
    return counts;
  }, [projects]);

  const visibleProjects = useMemo(
    () => (statusFilter === 'all' ? projects : projects.filter((p) => p.status === statusFilter)),
    [projects, statusFilter]
  );

  const goToDetail = (projectId) => navigate(`/projects/${projectId}`);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            <Button size="sm" variant={viewMode === 'grid' ? 'default' : 'ghost'} className="h-8 w-8 p-0" onClick={() => setViewMode('grid')} aria-label={t('gridView')} title={t('gridView')}>
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button size="sm" variant={viewMode === 'list' ? 'default' : 'ghost'} className="h-8 w-8 p-0" onClick={() => setViewMode('list')} aria-label={t('listView')} title={t('listView')}>
              <List className="h-4 w-4" />
            </Button>
          </div>
          {!isReadOnly && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openAddDialog} data-testid="add-project-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('addProject')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingProject ? t('form.editTitle') : t('form.addTitle')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">{t('form.name')}</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t('form.namePlaceholder')}
                      required
                      data-testid="project-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="domain">{t('form.domain')}</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="domain"
                          className="pl-9"
                          value={formData.domain}
                          onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                          placeholder="example.com"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="project_type">{t('form.type')}</Label>
                      <Select value={formData.project_type} onValueChange={(val) => setFormData({ ...formData, project_type: val })}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('form.selectType')} />
                        </SelectTrigger>
                        <SelectContent>
                          {PROJECT_TYPES.map((pt) => (
                            <SelectItem key={pt.value} value={pt.value}>{t(pt.labelKey)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('form.rating')}</Label>
                      <div className="mt-1.5">
                        <StarRating rating={formData.rating} onChange={(val) => setFormData({ ...formData, rating: val })} />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="status">{t('common:fields.status')}</Label>
                      <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                        <SelectTrigger data-testid="project-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">{t('status.active')}</SelectItem>
                          <SelectItem value="paused">{t('status.paused')}</SelectItem>
                          <SelectItem value="completed">{t('status.completed')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="merchant_name">{t('form.merchantName')}</Label>
                    <Input
                      id="merchant_name"
                      value={formData.merchant_name}
                      onChange={(e) => setFormData({ ...formData, merchant_name: e.target.value })}
                      placeholder={t('form.merchantPlaceholder')}
                    />
                    <p className="text-xs text-slate-500 mt-1"><Trans t={t} i18nKey="form.merchantHint" components={{ sep: <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded" />, ex: <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded" /> }} /></p>
                  </div>
                  <div>
                    <Label htmlFor="owner_employee_id">{t('form.owner')}</Label>
                    <Select
                      value={formData.owner_employee_id || 'none'}
                      onValueChange={(val) => setFormData({ ...formData, owner_employee_id: val === 'none' ? '' : val })}
                    >
                      <SelectTrigger id="owner_employee_id">
                        <SelectValue placeholder={t('form.noOwner')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— {t('form.noOwner')} —</SelectItem>
                        {employees.filter(e => e.status === 'active').map(emp => (
                          <SelectItem key={emp.employee_id} value={emp.employee_id}>
                            {emp.full_name} {emp.telegram_chat_id ? '📱' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">{t('form.ownerHint')}</p>
                  </div>
                  <div>
                    <Label htmlFor="description">{t('common:fields.description')}</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder={t('form.descriptionPlaceholder')}
                      data-testid="project-description-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" data-testid="submit-project-btn">
                      {editingProject ? t('common:actions.update') : t('common:actions.create')}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                      {t('common:actions.cancel')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Status tabs */}
      <div className="tab-strip flex items-center gap-1 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            data-testid={`project-status-tab-${tab.value}`}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2 whitespace-nowrap ${
              statusFilter === tab.value
                ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            {t(tab.labelKey)}
            <span className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-xs ${
              statusFilter === tab.value
                ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
            }`}>
              {statusCounts[tab.value] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Grid / List / Empty */}
      {visibleProjects.length === 0 ? (
        <div className="text-center py-16 text-slate-400 dark:text-slate-500" data-testid="projects-empty">
          {statusFilter === 'all' ? t('empty.none') : t('empty.inStatus', { status: t(STATUS_TABS.find(tab => tab.value === statusFilter)?.labelKey) })}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleProjects.map((project) => (
            <div
              key={project.project_id}
              onClick={() => goToDetail(project.project_id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') goToDetail(project.project_id); }}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-emerald-300 dark:hover:border-emerald-700 transition-all duration-200 p-5 cursor-pointer"
              data-testid={`project-card-${project.project_id}`}
            >
              {/* Header row */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {project.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[project.status] || STATUS_STYLES.completed}`}>
                      {t(`status.${project.status}`, { defaultValue: project.status })}
                    </span>
                    {project.project_type && (
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_STYLES[project.project_type] || TYPE_STYLES.other}`}>
                        {t(`types.${project.project_type}`, { defaultValue: project.project_type })}
                      </span>
                    )}
                  </div>
                </div>
                {!isReadOnly && (
                  <div className="flex gap-1 ml-2 flex-shrink-0">
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); openEditDialog(project); }} data-testid={`edit-project-${project.project_id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); handleDelete(project.project_id); }} data-testid={`delete-project-${project.project_id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Info row: domain, rating */}
              {(project.domain || project.rating > 0) && (
                <div className="flex items-center gap-3 mb-3 text-sm">
                  {project.domain && (
                    <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400 truncate">
                      <Globe className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="truncate">{project.domain}</span>
                    </div>
                  )}
                  {project.rating > 0 && (
                    <div className="flex-shrink-0">
                      <StarRating rating={project.rating} readonly />
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              <TruncatedText
                text={project.description || t('noDescription')}
                maxLength={80}
                className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 block"
              />
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('table.project')}</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden md:table-cell">{t('table.domain')}</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden sm:table-cell">{t('table.type')}</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden sm:table-cell">{t('table.rating')}</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common:fields.status')}</th>
                {!isReadOnly && <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common:fields.actions')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {visibleProjects.map((project) => (
                <tr key={project.project_id} onClick={() => goToDetail(project.project_id)} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer" data-testid={`project-row-${project.project_id}`}>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{project.name}</div>
                    {project.description && <TruncatedText text={project.description} maxLength={35} className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[200px] block" />}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 hidden md:table-cell">
                    {project.domain ? (
                      <div className="flex items-center gap-1">
                        <Globe className="h-3.5 w-3.5" />
                        <span className="truncate max-w-[150px]">{project.domain}</span>
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-4 hidden sm:table-cell">
                    {project.project_type ? (
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_STYLES[project.project_type] || TYPE_STYLES.other}`}>
                        {t(`types.${project.project_type}`, { defaultValue: project.project_type })}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-4 hidden sm:table-cell">
                    <div className="flex justify-center">
                      {project.rating > 0 ? <StarRating rating={project.rating} readonly /> : <span className="text-xs text-slate-400">—</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[project.status] || STATUS_STYLES.completed}`}>
                      {t(`status.${project.status}`, { defaultValue: project.status })}
                    </span>
                  </td>
                  {!isReadOnly && (
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditDialog(project); }} data-testid={`edit-project-${project.project_id}`}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDelete(project.project_id); }} data-testid={`delete-project-${project.project_id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Projects;
